import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { getAccess } from '../services/authService';
import LogoMark from '../components/ui/LogoMark';

function PulseWaveIcon({ size = 20, color = '#fff' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <polyline points="4,16 8,16 10,9 13,23 16,12 19,20 22,16 28,16"
        stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const allProducts = [
  {
    id: 'pulse',
    name: 'Pulse',
    tagline: 'AI Support Agent',
    desc: 'AI-powered chatbot trained on your docs and workflows. Handles customer queries, captures leads, and hands off to humans when needed.',
    accent: '#479EC3',
    accentB: '#22D3EE',
    dashboardPath: '/dashboard/pulse',
    tags: ['AI Chatbot', 'No-code', '24/7'],
  },
];

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};
const fadeUp = {
  hidden:  { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22,1,0.36,1] } },
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [accessData, setAccessData] = useState({ emailVerified: true, items: [] });
  const [accessLoading, setAccessLoading] = useState(true);

  useEffect(() => {
    if (!user) { setAccessLoading(false); return; }
    setAccessLoading(true);
    getAccess()
      .then(setAccessData)
      .catch(() => setAccessData({ emailVerified: true, items: [] }))
      .finally(() => setAccessLoading(false));
  }, [user]);

  const access = accessData.items;
  const emailVerified = accessData.emailVerified;

  if (accessLoading) return (
    <div style={{ minHeight: '100vh', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <div style={{ width: 36, height: 36, border: '3px solid rgba(71,158,195,0.2)', borderTop: '3px solid #479EC3', borderRadius: '50%', animation: 'reano-spin 0.7s linear infinite' }} />
        <span style={{ fontSize: 14, color: '#475569' }}>Loading your dashboard…</span>
      </div>
      <style>{`@keyframes reano-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  return (
    <div style={{ background: '#0f172a', color: '#e2e8f0', minHeight: '100vh', fontFamily: 'Inter, system-ui, sans-serif', position: 'relative', overflow: 'hidden' }}>
      {/* Background grid */}
      <div style={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'linear-gradient(to right,#22D3EE 1px,transparent 1px),linear-gradient(to bottom,#22D3EE 1px,transparent 1px)', backgroundSize: '64px 64px', pointerEvents: 'none' }} />
      {/* Glow */}
      <div style={{ position: 'absolute', top: 0, left: '30%', width: 600, height: 400, background: 'radial-gradient(ellipse,rgba(71,158,195,0.08) 0%,transparent 70%)', pointerEvents: 'none' }} />

      {/* Top bar */}
      <style>{`
        @media (max-width: 540px) {
          .db-topbar-inner { padding: 0 14px !important; gap: 8px !important; }
          .db-topbar-info { max-width: 140px; }
          .db-topbar-info > span { display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
          .db-topbar-divider { display: none !important; }
          .db-signout-label { display: none !important; }
        }
      `}</style>
      <div style={{ position: 'sticky', top: 0, zIndex: 100, background: 'rgba(15,23,42,0.85)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="db-topbar-inner" style={{ maxWidth: 1200, margin: '0 auto', padding: '0 28px', height: 64, display: 'flex', alignItems: 'center', gap: 12 }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg,#479EC3,#22D3EE)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <LogoMark />
            </div>
            <span style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-.025em', color: '#fff' }}>
              Reano <span style={{ color: '#479EC3' }}>AI</span>
            </span>
          </Link>

          <div style={{ flex: 1 }} />

          <div className="db-topbar-info" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            {user?.companyName && (
              <span style={{ fontSize: 12.5, fontWeight: 600, color: '#64748b' }}>
                {user.companyName}
                {user.companyId && <span style={{ color: '#334155', fontWeight: 400, marginLeft: 6 }}>{user.companyId}</span>}
              </span>
            )}
            <span style={{ fontSize: 12, color: '#475569' }}>{user?.email}</span>
          </div>
          <div className="db-topbar-divider" style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.1)' }} />
          <button onClick={logout} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            fontSize: 13.5, fontWeight: 600, color: '#64748b',
            display: 'flex', alignItems: 'center', gap: 5,
            transition: 'color .15s',
          }}
            onMouseEnter={e => e.currentTarget.style.color = '#94a3b8'}
            onMouseLeave={e => e.currentTarget.style.color = '#64748b'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            <span className="db-signout-label">Sign Out</span>
          </button>
        </div>
      </div>

      <main style={{ maxWidth: 1200, margin: '0 auto', padding: '52px 28px 80px', position: 'relative', zIndex: 1 }}>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} style={{ marginBottom: 52 }}>
          <p style={{ fontSize: 13, color: '#479EC3', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>Dashboard</p>
          <h1 style={{ fontSize: 'clamp(24px,3vw,36px)', fontWeight: 800, letterSpacing: '-.03em', marginBottom: 6 }}>
            Welcome back,{' '}
            <span style={{ background: 'linear-gradient(135deg,#479EC3,#22D3EE)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {user?.name?.split(' ')[0]}
            </span>
          </h1>
          <p style={{ fontSize: 15, color: '#475569' }}>Manage and access your Reano AI products below.</p>
        </motion.div>

        {/* Email not verified banner */}
        {!emailVerified && (
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            style={{
              marginBottom: 16, borderRadius: 14,
              background: 'linear-gradient(135deg, rgba(234,179,8,0.07), rgba(234,179,8,0.04))',
              border: '1px solid rgba(234,179,8,0.25)',
              padding: '16px 22px',
              display: 'flex', alignItems: 'flex-start', gap: 14,
            }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
            <div>
              <p style={{ fontSize: 14, fontWeight: 700, color: '#fef08a', marginBottom: 4 }}>
                Please verify your email
              </p>
              <p style={{ fontSize: 13.5, color: '#64748b', lineHeight: 1.6 }}>
                We sent a verification link to <strong style={{ color: '#94a3b8' }}>{user?.email}</strong>. Check your inbox and click the link to activate your account.
              </p>
            </div>
          </motion.div>
        )}

        {/* Approval pending banner */}
        {!user?.isActive && (
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}
            style={{
              marginBottom: 36, borderRadius: 14,
              background: 'linear-gradient(135deg, rgba(71,158,195,0.08), rgba(34,211,238,0.06))',
              border: '1px solid rgba(71,158,195,0.25)',
              padding: '18px 22px',
              display: 'flex', alignItems: 'flex-start', gap: 14,
            }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              background: 'rgba(71,158,195,0.12)', border: '1px solid rgba(71,158,195,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#479EC3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <div>
              <p style={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0', marginBottom: 4 }}>
                Your account is pending activation
              </p>
              <p style={{ fontSize: 13.5, color: '#64748b', lineHeight: 1.6 }}>
                The Reano team is reviewing your signup and will reach out within 24 hours to get you set up. Check your inbox — we'll notify you the moment you're approved.
              </p>
            </div>
          </motion.div>
        )}

        {/* Section label */}
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
          style={{ fontSize: 13, color: '#334155', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 20 }}>
          Your Products
        </motion.p>

        {/* Product grid */}
        <motion.div variants={stagger} initial="hidden" animate="visible"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>

          {allProducts.map(p => {
            const accessEntry = access.find(a => a.productId === p.id && a.status === 'active');
            const hasAccess = !!accessEntry;
            const planLabel = accessEntry?.plan
              ? accessEntry.plan.charAt(0).toUpperCase() + accessEntry.plan.slice(1)
              : null;
            const periodEnd = accessEntry?.periodEnd
              ? new Date(accessEntry.periodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
              : null;
            const seats = accessEntry?.seats ?? null;
            return (
              <motion.div key={p.id} variants={fadeUp}
                style={{
                  background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(20px)',
                  border: `1px solid ${hasAccess ? 'rgba(71,158,195,0.25)' : 'rgba(30,41,59,0.8)'}`,
                  borderTop: `3px solid ${hasAccess ? p.accent : 'rgba(30,41,59,0.8)'}`,
                  borderRadius: 16, padding: 28,
                  display: 'flex', flexDirection: 'column',
                  opacity: hasAccess ? 1 : 0.55,
                }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18 }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: 12,
                    background: hasAccess ? `linear-gradient(135deg,${p.accent},${p.accentB})` : 'rgba(30,41,59,0.8)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: hasAccess ? `0 4px 16px ${p.accent}44` : 'none',
                  }}>
                    <PulseWaveIcon color={hasAccess ? '#fff' : '#334155'} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                    <span style={{
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
                      padding: '4px 10px', borderRadius: 6,
                      color: hasAccess ? '#10b981' : '#334155',
                      background: hasAccess ? 'rgba(16,185,129,0.1)' : 'rgba(30,41,59,0.6)',
                      border: `1px solid ${hasAccess ? 'rgba(16,185,129,0.3)' : 'rgba(30,41,59,0.8)'}`,
                    }}>
                      {hasAccess ? 'Active' : 'No Access'}
                    </span>
                    {planLabel && (
                      <span style={{
                        fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
                        padding: '3px 8px', borderRadius: 6,
                        color: '#479EC3', background: 'rgba(71,158,195,0.1)', border: '1px solid rgba(71,158,195,0.25)',
                      }}>
                        {planLabel}
                      </span>
                    )}
                  </div>
                </div>

                <h3 style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-.03em', color: '#f1f5f9', marginBottom: 2 }}>{p.name}</h3>
                <p style={{ fontSize: 12.5, fontWeight: 600, color: hasAccess ? p.accent : '#334155', marginBottom: 10 }}>{p.tagline}</p>
                <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.65, flex: 1, marginBottom: 16 }}>{p.desc}</p>

                {hasAccess && (periodEnd || seats) && (
                  <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                    {seats && (
                      <div style={{ fontSize: 12, color: '#475569' }}>
                        <span style={{ color: '#94a3b8', fontWeight: 600 }}>{seats}</span> seats
                      </div>
                    )}
                    {periodEnd && (
                      <div style={{ fontSize: 12, color: '#475569' }}>
                        Renews <span style={{ color: '#94a3b8', fontWeight: 600 }}>{periodEnd}</span>
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
                  {p.tags.map(tag => (
                    <span key={tag} style={{
                      fontSize: 11.5, fontWeight: 500, color: '#475569',
                      background: 'rgba(30,41,59,0.6)', border: '1px solid rgba(30,41,59,0.8)',
                      borderRadius: 5, padding: '3px 9px',
                    }}>{tag}</span>
                  ))}
                </div>

                {!emailVerified ? (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: 13, fontWeight: 500 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="10" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                    <span style={{ color: '#ca8a04' }}>Verify your email to continue</span>
                  </div>
                ) : !user?.isActive ? (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: 13, fontWeight: 500 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#479EC3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                    </svg>
                    <span style={{ color: '#479EC3' }}>Pending approval</span>
                  </div>
                ) : hasAccess ? (
                  <Link to={p.dashboardPath} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '10px 18px', borderRadius: 9, alignSelf: 'flex-start',
                    background: `linear-gradient(135deg,${p.accent},${p.accentB})`,
                    color: '#fff', fontSize: 14, fontWeight: 700,
                    textDecoration: 'none', boxShadow: `0 0 20px ${p.accent}44`,
                    transition: 'opacity .2s',
                  }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                  >
                    Open Dashboard
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 6.5h9M7.5 3l3 3.5-3 3"/></svg>
                  </Link>
                ) : (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#334155', fontSize: 13, fontWeight: 500 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="10" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                    Contact support to get access
                  </div>
                )}
              </motion.div>
            );
          })}

          {/* Coming soon */}
          <motion.div variants={fadeUp}
            style={{
              background: 'rgba(15,23,42,0.4)', backdropFilter: 'blur(20px)',
              border: '1px solid rgba(30,41,59,0.6)', borderTop: '3px solid rgba(99,102,241,0.3)',
              borderRadius: 16, padding: 28, display: 'flex', flexDirection: 'column', opacity: 0.5,
            }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18 }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(30,41,59,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#334155" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', padding: '4px 10px', borderRadius: 6, color: '#8b5cf6', background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.2)' }}>
                Coming Soon
              </span>
            </div>
            <h3 style={{ fontSize: 20, fontWeight: 800, color: '#f1f5f9', marginBottom: 2 }}>More Products</h3>
            <p style={{ fontSize: 12.5, fontWeight: 600, color: '#334155', marginBottom: 10 }}>In development</p>
            <p style={{ fontSize: 14, color: '#334155', lineHeight: 1.65 }}>New AI-powered products are on the way. You'll be notified when they become available.</p>
          </motion.div>
        </motion.div>

      </main>
    </div>
  );
}
